import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Upload,
  Card,
  Row,
  Col,
  Table,
} from "antd";
import {
  DeleteOutlined,
  LinkOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import PageTitle from "../PageTitle";
import { useParams } from "react-router-dom";
import useTenantNavigate from "../../hooks/useTenantNavigate";
import FileUploadModal from "../FileUploadModal";
import { toast } from "react-toastify";
import axios from "axios";
import moment from "moment";
import { getNoticeDetails, postData } from "../../api/apiService";
import { FILE_PREFIX, getFileUrl } from "../../utils/constants";
import { CLIENTS } from "../../api/endpointMap";
import { getEmbedToken } from "../../api/api";

const { Option } = Select;

const AddEproceedingForm = ({ serviceType }) => {
  const [form] = Form.useForm();
  const [replyData, setReplyData] = useState([]);
  const { clientId } = useParams();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedUploadType, setSelectedUploadType] = useState(null);
  const [noticeLetter, setNoticeLetter] = useState(null);
  const [responseAcknowledgement, setResponseAcknowledgement] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);

  const navigate = useTenantNavigate();

  const onFinish = async (values) => {
    console.log("Form Values:", values);

    if (!clientDetails?.id) {
      toast.error("Client details not loaded yet. Please wait a moment and try again.");
      return;
    }

    const payload = {
      ...values,
      response_due_date: values.response_due_date
        ? values.response_due_date.format("YYYY-MM-DD")
        : null,
      notice_sent_date: values.notice_sent_date
        ? values.notice_sent_date.format("YYYY-MM-DD")
        : null,
      // notice_letter / response_acknowledgement are stored in component state
      // (not antd form internal store) because their `Form.Item` children are
      // <div>/<Button> not form-controls — antd's `setFieldsValue` populates the
      // store but onFinish `values` doesn't extract values from non-control
      // children. Pulling from state directly here is the only reliable path.
      notice_letter: noticeLetter || null,
      response_acknowledgement: responseAcknowledgement || null,
      replies: replyData.map((reply) => ({
        file_name: reply.file_name,
        file: reply.file,
        key: reply.key,
      })),
      client: clientDetails.id,
    };

    try {
      setLoading(true);

      // Backend endpoint for manually creating an e-proceeding (added in #77).
      // Replaces the old `createNotice("E Proceeding", payload)` call which
      // POSTed to `/E Proceeding` (Frappe REST style) — that path returned 404.
      await postData("/api/v1/income-tax/e-proceedings", payload);

      toast.info("Notice Added Successfully", {
        position: "top-right",
        autoClose: 5000,
      });

      setTimeout(() => {
        navigate(`/income-tax/eproceedings/${clientId}?noticeType=active`);
      }, 500);
    } catch (err) {
      toast.error("Error in Creating notice ", {
        position: "top-right",
        autoClose: 5000,
      });
      console.log("Error", err);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleUpload = async (file) => {
    // Reply files: enforce the 8 MB TOTAL across all reply rows before
    // uploading (per-file 4 MB is enforced by FileUploadModal). Checked here
    // because each reply row uploads through its own modal, so the modal alone
    // can't see the other rows' files.
    if (selectedUploadType?.startsWith("reply")) {
      const replyIndex = parseInt(selectedUploadType.split("-")[1], 10);
      const otherReplyBytes = replyData.reduce(
        (sum, r, i) => (i === replyIndex ? sum : sum + (r.size || 0)),
        0
      );
      if (otherReplyBytes + (file.size || 0) > MAX_REPLY_TOTAL_BYTES) {
        toast.error(
          `Adding this file would exceed the total reply limit of ${
            MAX_REPLY_TOTAL_BYTES / (1024 * 1024)
          } MB.`,
          { position: "top-right", autoClose: 5000 }
        );
        handleModalClose();
        return;
      }
    }

    try {
      setUploadLoading(true);

      const payload = new FormData();
      payload.append("is_private", "0");
      // payload.append("doctype", "E Proceeding");
      // payload.append("docname", clientId);
      payload.append("file", file, file.name);

      const token = getEmbedToken() || localStorage.getItem("authToken") || "";
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        process.env.REACT_APP_API_BASE_URL + "/api/v1/income-tax/upload",
        payload,
        config
      );

      const fileURL = response?.data?.file_url;

      if (fileURL) {
        // Store as `{file_url, file_name}` dict so the original filename
        // can be persisted to the backend's companion column
        // (e_proceedings.notice_letter_file_name from #81). Backend's create
        // endpoint accepts both string and dict shapes.
        const fileObject = { file_url: fileURL, file_name: file.name };
        if (selectedUploadType == "notice_letter") {
          form.setFieldsValue({ notice_letter: fileObject });
          setNoticeLetter(fileObject);
        } else if (selectedUploadType == "response_acknowledgement") {
          form.setFieldsValue({ response_acknowledgement: fileObject });
          setResponseAcknowledgement(fileObject);
        } else if (selectedUploadType?.startsWith("reply")) {
          const index = parseInt(selectedUploadType.split("-")[1], 10);
          const updatedReplyData = [...replyData];
          updatedReplyData[index].file = fileURL;
          // Track the file size so the 8 MB total guard can sum across rows.
          updatedReplyData[index].size = file.size || 0;
          setReplyData(updatedReplyData);
        }
      }

      const allValues = form.getFieldsValue();

      toast.info("File uploaded successfully", {
        position: "top-right",
        autoClose: 5000,
      });

      handleModalClose();
    } catch (error) {
      toast.error("Error uploading file", {
        position: "top-right",
        autoClose: 5000,
      });
      console.error("Error:", error);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedUploadType(null);
  };

  // Reply attachment caps (same rule as the GST appeal supporting docs /
  // ManualOrderForm): at most 2 files, 4 MB per file, 8 MB total. Per-file
  // 4 MB is enforced by FileUploadModal's defaults; the count + cross-row
  // total are enforced here because each reply row uploads independently.
  const MAX_REPLY_FILES = 2;
  const MAX_REPLY_TOTAL_BYTES = 8 * 1024 * 1024;

  const handleAddRow = () => {
    if (replyData.length >= MAX_REPLY_FILES) {
      toast.warning(`You can add at most ${MAX_REPLY_FILES} replies.`, {
        position: "top-right",
        autoClose: 4000,
      });
      return;
    }
    const newRow = { key: replyData.length + 1, file_name: "", file: null, size: 0 };
    setReplyData([...replyData, newRow]);
  };

  const handleEditRow = (index, field, value) => {
    const newData = [...replyData];
    newData[index][field] = value;
    setReplyData(newData);
  };

  const columns = [
    { title: "No.", dataIndex: "key", key: "key" },
    {
      title: "File Name",
      dataIndex: "file_name",
      key: "file_name",
      render: (text, record, index) => (
        <Input
          value={text}
          placeholder="File Name"
          onChange={(e) => handleEditRow(index, "file_name", e.target.value)}
        />
      ),
    },
    {
      title: "File",
      dataIndex: "file",
      key: "file",
      render: (text, record, index) =>
        record.file ? (
          <div className="flex items-center space-x-2">
            <a href={record.file} target="_blank" rel="noopener noreferrer">
              <Button icon={<LinkOutlined />}>View</Button>
            </a>
            <Button
              icon={<DeleteOutlined />}
              onClick={() => {
                const updatedReplies = [...replyData];
                updatedReplies[index].file = null;
                updatedReplies[index].size = 0;
                setReplyData(updatedReplies);
              }}
            />
          </div>
        ) : (
          <Button
            icon={<UploadOutlined />}
            onClick={() => {
              setSelectedUploadType(`reply-${index}`);
              setIsModalVisible(true);
            }}
          >
            Attach
          </Button>
        ),
    },
  ];

  useEffect(() => {
    if (!clientId) return;

    // Use the migrated GET endpoint instead of the legacy `useAxiosPost` POST
    // to `/clients/income-tax/details` (which doesn't exist on the FastAPI
    // backend — same bug fixed in #75 for EPForm.jsx).
    const basePathMap = {
      gstin: "gst",
      "income-tax": "income-tax",
      tds: "tds",
    };
    const basePath = basePathMap[serviceType] || "income-tax";
    const endpoint = `/api/v1/clients/${basePath}/${clientId}`;

    getNoticeDetails(endpoint)
      .then((data) => {
        // GET /clients/{type}/{id} returns the client dict directly with
        // `disabled` as a boolean. Normalize to number to match legacy
        // comparison patterns elsewhere.
        const normalized = data
          ? { ...data, disabled: data.disabled ? 1 : 0 }
          : { disabled: 0 };
        setClientDetails(normalized);
      })
      .catch((err) => {
        console.error("Failed to fetch client details:", err);
        setClientDetails({ disabled: 0 });
      });
  }, [clientId, serviceType]);

  useEffect(() => {
    if (clientDetails) {
      form.setFieldValue("client", clientDetails?.client_name);
    }
  }, [clientDetails]);

  return (
    <div>
      <PageTitle title="New E Proceeding" />
      <div className="min-h-screen flex flex-col items-center justify-center bg-blue-100 p-4">
        <div className="w-full max-w-5xl bg-white shadow-lg rounded-2xl p-6">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            className="w-full"
          >
            <Card
              title="Notice Details"
              bordered={false}
              style={{ border: "4px solid #f5f5f5", marginBottom: "16px" }}
              headStyle={{ borderBottom: "3px solid #f5f5f5" }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Proceeding Name"
                    name="proceeding_name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter proceeding name",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Financial Year" name="financial_year">
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Notice/ Communication Reference ID"
                    name="notice_din"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Assessment Year" name="assessment_year">
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Document Reference ID"
                    name="document_reference_id"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Proceeding Status" name="proceeding_status">
                    <Select style={{ height: "42px" }}>
                      <Option value="Open">Open</Option>
                      <Option value="Close">Close</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Notice Section" name="notice_section">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Client" name="client">
                    <Input disabled />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Response Due Date" name="response_due_date" rules={[{ required: true, message: "Please select a due date" }]}>
                    <DatePicker
                      style={{ width: "100%" }}
                      format={"DD/MM/YYYY"}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Notice Sent Date" name="notice_sent_date">
                    <DatePicker
                      style={{ width: "100%" }}
                      format={"DD/MM/YYYY"}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card
              title="Documents"
              bordered={false}
              headStyle={{ borderBottom: "3px solid #f5f5f5" }}
              style={{ border: "4px solid #f5f5f5", marginBottom: "16px" }}
            >
              <Row gutter={[16, 16]}>
                {/* <Col xs={24} sm={12}>
                  <Form.Item
                    label="Response Acknowledgement"
                    name="response_acknowledgement"
                  >
                    {responseAcknowledgement ? (
                      <div className="flex items-center space-x-2">
                        <a
                          href={responseAcknowledgement}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button icon={<LinkOutlined />}>
                            {" "}
                            <span>
                              {responseAcknowledgement?.split("/")?.pop()}
                            </span>
                          </Button>
                        </a>

                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            setResponseAcknowledgement(null);
                            form.setFieldsValue({
                              response_acknowledgement: "",
                            });
                          }}
                        />
                      </div>
                    ) : (
                      <Button
                        onClick={() => {
                          setSelectedUploadType("response_acknowledgement");
                          setIsModalVisible(true);
                        }}
                        icon={<UploadOutlined />}
                      >
                        Attach
                      </Button>
                    )}
                  </Form.Item>
                </Col> */}
                <Col xs={24} sm={12}>
                  <Form.Item label="Notice Letter" name="notice_letter">
                    {noticeLetter ? (
                      <div className="flex items-center space-x-2">
                        <a
                          href={getFileUrl(noticeLetter?.file_url || noticeLetter)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button icon={<LinkOutlined />}>
                            {" "}
                            <span>
                              {noticeLetter?.file_name ||
                                (typeof noticeLetter === "string"
                                  ? noticeLetter.split("/").pop()
                                  : "")}
                            </span>
                          </Button>
                        </a>

                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            setNoticeLetter(null);
                            form.setFieldsValue({ notice_letter: "" });
                          }}
                        />
                      </div>
                    ) : (
                      <Button
                        onClick={() => {
                          setSelectedUploadType("notice_letter");
                          setIsModalVisible(true);
                        }}
                        icon={<UploadOutlined />}
                      >
                        Attach
                      </Button>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card
              title="Replies"
              bordered={false}
              headStyle={{ borderBottom: "3px solid #f5f5f5" }}
              style={{ border: "4px solid #f5f5f5", marginBottom: "16px" }}
            >
              <Table
                columns={columns}
                dataSource={replyData}
                pagination={false}
                bordered
              />
              <Button
                onClick={handleAddRow}
                icon={<PlusOutlined />}
                className="mt-4"
                disabled={replyData.length >= MAX_REPLY_FILES}
                title={
                  replyData.length >= MAX_REPLY_FILES
                    ? `Maximum ${MAX_REPLY_FILES} replies (4 MB each, 8 MB total)`
                    : undefined
                }
              >
                Add Previous Reply
              </Button>
            </Card>

            <div className="flex justify-end mt-4">
              <Button disabled={loading} type="primary" htmlType="submit">
                {loading ? "Adding.." : "Add Notice"}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <FileUploadModal
        visible={isModalVisible}
        onClose={handleModalClose}
        onUpload={handleUpload}
        disabled={uploadLoading}
      />
    </div>
  );
};

export default AddEproceedingForm;
